/* eslint-disable camelcase */
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    addGiftCertBasketInformation,
    getGiftCertBasket,
    getGiftCertBasketIdCookie,
    getGiftCertBasketPayment,
    giftCertBasketSetup,
} from './utils';

import PropTypes from 'prop-types';
import { transformAndFetchGiftCertificates } from './dataTransformers/giftCertificate';
import { useSelector } from 'react-redux';

const GiftCertificateContext = createContext({ state: null, setState: () => {} });

const GiftCertificateProvider = ({ children, giftCertIdsFromCms }) => {
    const application = useSelector(state => state.application);

    const [state, setState] = useState({
        selectedGiftCert: null,
        giftCerts: [],
        sectionLoading: 'giftCerts',
        giftCertCheckoutStep: 1,
        giftCertBasket: null,
    });

    const onSelect = cert => {
        setState(prevState => ({ ...prevState, selectedGiftCert: cert }));
    };

    useEffect(() => {
        (async () => {
            const transformedGiftCerts = await transformAndFetchGiftCertificates(giftCertIdsFromCms);
            setState(prevState => ({ ...prevState, sectionLoading: null, giftCerts: transformedGiftCerts }));
        })();
    }, [giftCertIdsFromCms]);

    const { selectedGiftCert, giftCerts, sectionLoading, giftCertCheckoutStep, giftCertBasket } = state;

    const transformResponseDataBasket = data => ({
        address: data.address || null,
        basketId: data.id,
        centraCheckoutScript: data.centra_checkout_script || null,
        country: data.country || null,
        currency: data.currency || null,
        currencyFormat: data.currency_format || null,
        items: data.items || [],
        paymentMethodId: data.payment_method_id || null,
        paymentMethods: data.payment_methods || null,
        shippingMethodId: data.shipping_method_id || null,
        shippingMethods: data.shipping_methods || null,
        totalDiscount: data.total_discount || null,
        totals: data.totals || [],
        addBasketInformation: handleAddGiftCertBasketInformation,
        getBasketIdCookie: getGiftCertBasketIdCookie,
        getBasketPayment: getGiftCertBasketPayment,
    });

    const handleAddGiftCertBasketInformation = async data => {
        const response = await addGiftCertBasketInformation(data);
        const transformedBasket = transformResponseDataBasket(response.data);
        setState(prevState => ({ ...prevState, giftCertBasket: transformedBasket }));
        return response;
    };

    const proceedToCheckout = async () => {
        setState(prevState => ({ ...prevState, sectionLoading: 'checkout' }));
        const basketSetupResponse = await giftCertBasketSetup(application, selectedGiftCert);

        if (basketSetupResponse.status === 200 || basketSetupResponse.status === 201) {
            const getBasketResponse = await getGiftCertBasket();

            if (getBasketResponse.status === 200 || getBasketResponse.status === 201) {
                const transformedBasket = transformResponseDataBasket(getBasketResponse.data);

                setState(prevState => ({
                    ...prevState,
                    giftCertCheckoutStep: 2,
                    giftCertBasket: transformedBasket,
                    sectionLoading: null,
                }));
            } else {
                setState(prevState => ({
                    ...prevState,
                    sectionLoading: null,
                    errorMessage: 'Error when fetching basket',
                }));
            }
        } else {
            setState(prevState => ({
                ...prevState,
                sectionLoading: null,
                errorMessage: 'Error when loading checkout',
            }));
        }
    };

    const editSelectedGiftCertificate = () => {
        setState(prevState => ({ ...prevState, giftCertCheckoutStep: 1 }));
    };

    // Check if we're ready to proceed to Checkout
    const readyToProceed =
        !!selectedGiftCert &&
        (selectedGiftCert.type !== 'anyAmount' ||
            (selectedGiftCert.type === 'anyAmount' && selectedGiftCert.flexibleAmount));

    return (
        <GiftCertificateContext.Provider
            value={{
                editSelectedGiftCertificate,
                giftCertBasket,
                giftCertCheckoutStep,
                giftCerts,
                onSelect,
                proceedToCheckout,
                readyToProceed,
                sectionLoading,
                selectedGiftCert,
            }}
        >
            {children}
        </GiftCertificateContext.Provider>
    );
};

GiftCertificateProvider.propTypes = {
    giftCertIdsFromCms: PropTypes.arrayOf(
        PropTypes.shape({
            gift_certificate: PropTypes.arrayOf(PropTypes.string),
            gift_certificate_display_name: PropTypes.string,
        })
    ),
};

const useGiftCertificateContext = () => {
    const context = useContext(GiftCertificateContext);
    return context || { error: 'GiftCertificateContext not available' };
};

export { GiftCertificateContext as default, GiftCertificateProvider, useGiftCertificateContext };
